import { DeleteAction, SaveAction } from "./action";
import { Toolbar, ToolbarItem } from "./toolbar";
import { CircleTool, DragTool, PencilTool, RectangleTool, SelectionTool, ToolBase } from "./tool";
import { BaseModule, StyleModule } from "./module";
import { ClearAction } from "./action/clearaction";
import { LineTool } from "./tool/linetool";
import { ArrowTool } from "./tool/arrowtool";
import { TextTool } from "./tool/texttool";
import { EraserTool } from "./tool/erasertool";
import { CalibratorTool } from "./tool/calibratortool";
import { ToggleToolbarItem } from "./toolbar/toggletoolbaritem";

const fabric = require("./fabric").fabric;

export class ImageEditor {

  /**
   * @type {String}
   */
  currentMode = "selection";

  /**
   * @type {HTMLElement}
   */
  element = null;

  /**
   * @type {HTMLElement}
   */
  toolbarEl = null;

  /**
   * @type {fabric.Canvas}
   */
  canvas = null;

  /**
   * @type {HTMLElement}
   */
  canvasEl = null;

  /**
   * @type {Toolbar}
   */
  toolbar = null;

  /**
   * @type {ToolBase[]}
   */
  tools = [];

  /**
   * @type {ActionBase[]}
   */
  actions = [];

  /**
   * Active modules
   */
  modules = {};

  /**
   * @type {number}
   */
  mesurementPerPixel = 0;

  /**
   * @type {number}
   */
  mesurementUnit = "un";

  /**
   * Constructeur
   * @param {HTMLElement|String} el 
   * @param {Object} options 
   */
  constructor(el, options) {
    this.canvasEl = document.querySelector(el);

    this.canvas = new fabric.Canvas(this.canvasEl, {
      width: this.canvasEl.clientWidth,
      height: this.canvasEl.clientHeight,
      backgroundColor: "white",
      selection: false
    });

    this.setBackgroundImage(options.backgroundImage, () => {
      let bgimage = this.canvas.backgroundImage;
      this.canvas.setZoom(this.canvasEl.clientHeight / bgimage.height);
    });

    this.registerModule("style", new StyleModule(this));

    this.tools.push(new SelectionTool(this, "selection"));
    this.tools.push(new DragTool(this, "drag"));
    this.tools.push(new LineTool(this, "line"));
    this.tools.push(new RectangleTool(this, "rectangle"));
    this.tools.push(new CircleTool(this, "circle"));
    this.tools.push(new ArrowTool(this, "arrow"));
    this.tools.push(new TextTool(this, "text"));
    this.tools.push(new PencilTool(this, "pencil"));
    this.tools.push(new EraserTool(this, "eraser"));
    this.tools.push(new CalibratorTool(this, "calibrator"));
    this.actions.push(new DeleteAction(this, "delete"));
    this.actions.push(new SaveAction(this, "save"));
    this.actions.push(new ClearAction(this, "clear"));

    if (options.toolbar !== undefined) {
      if (options.toolbar.el instanceof HTMLElement) {
        this.toolbarEl = options.toolbar.el;
      } else {
        this.toolbarEl = document.querySelector(options.toolbar.el);
      }

      this.toolbar = new Toolbar(this, this.toolbarEl)

      for (const item of options.toolbar.items) {
        let toolbarItem = null;

        if(item.type === "tool") {
          toolbarItem = new ToolbarItem(this, item.name, item.el);
          toolbarItem.tool = this.tools.find(t => t.name == item.name);
        } else if (item.type == "action") {
          toolbarItem = new ToolbarItem(this, item.name, item.el);
          toolbarItem.action = this.actions.find(t => t.name == item.name);
        } else if (item.type == "toggle") {
          toolbarItem = new ToggleToolbarItem(this, item.name, item.el);
        }
        
        if (item.onClick != undefined) {
          toolbarItem.onClickCallback = item.onClick;
        }

        this.toolbar.addItem(toolbarItem);
      }
    }


    this.registerEvents();
  }

  isToolbarToggleActive(name) {
    return this.toolbar.items.find(t => t.name == name).isActive
  }

  /**
   * Sets the background image of the image editor.
   * @param {String} src Image path
   * @param {Function} callback Callback
   */
  setBackgroundImage(src, callback) {
    this.canvas.setBackgroundImage(src, callback, {
      originX: 'left',
      originY: 'top'
    });
  }

  /**
   * Registers a module to the image editor.
   * @param {String} name Name of the module
   * @param {BaseModule} module Module object
   */
  registerModule(name, module) {
    this.modules[name] = module;
  }

  /**
   * Get the registered module.
   * @param {String} name Name of the registered module.
   * 
   * @returns {BaseModule}
   */
  getModule(name) {
    return this.modules[name];
  }

  registerEvents() {
    var canvas = this.canvas;

    // Zooming

    canvas.on('mouse:wheel', function (opt) {
      var delta = opt.e.deltaY;
      var zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      if (zoom > 20) zoom = 20;
      if (zoom < 0.01) zoom = 0.01;
      canvas.zoomToPoint(new fabric.Point(opt.pointer.x, opt.pointer.y), zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
      canvas.requestRenderAll();
    })

  }

  calculateMesurementValue(mesuredPixels) {
    return mesuredPixels / this.mesurementPerPixel;
  }

  showMesurementCalibrator(mesuredPixels, lineCalibrator) {

    console.log("Mesured: " + this.calculateMesurementValue(mesuredPixels));

    bootbox.dialog({
      title: "Calibrer mesures",
      message: `
        <div class="calibrator-form" style="display: flex; grid-gap: 5px">
          <div>
            <label for="calibrator-amount">Distance:</label>
            <input type="text" id="calibrator-amount" class="form-control" />
          </div>
          <div>
            <label for="calibrator-unit">Unité:</label>
            <select class="form-control" id="calibrator-unit">
              <option value="pi">Pieds</option>
              <option value="po">Pouces</option>
              <option value="m">Mètres</option>
              <option value="cm">Centimètres</option>
            </select>
          </div>
        </div>
      `,
      buttons: {
        ok: {
          label: "Confirmer",
          className: "btn purple",
          callback: () => {
            var distance = parseFloat(document.querySelector("#calibrator-amount").value);
            var unit = document.querySelector("#calibrator-unit").value;
            this.mesurementPerPixel = mesuredPixels / distance;
            this.mesurementUnit = unit;
            this.canvas.remove(lineCalibrator);
          }
        },
        cancel: {
          label: "Annuler",
          className: "btn secondary",
          callback: () => {
            this.canvas.remove(lineCalibrator);
          }
        }
      }
    })
  }

}